import { lazySetupMailListItemContainerKeys } from "./lazy/lazyListViewModule";
import type { MailListItemDataProps, MailListTableProps } from "owa-mail-list-item-shared";
import { onMailListItemClickHandler, MailListItemContainerContextProvider, getIsChecked, getBackgroundColorClass, } from "owa-mail-list-item-shared";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { observer } from "owa-mobx-react";
import type { AriaProperties } from "owa-accessibility";
import { AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import Draggable from "owa-dnd/lib/components/Draggable";
import { DraggableItemTypes } from "owa-dnd/lib/utils/DraggableItemTypes";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { getIsMsHighContrast } from "owa-high-contrast";
import { useKeydownHandler, useLazyKeydownHandler } from "owa-hotkeys";
import loc from "owa-localize";
import { noSubject } from "owa-locstrings/lib/strings/nosubject.locstring.json";
import { type FocusComponent, setFocusToSynchronous, tabIndex } from "owa-mail-focus-manager";
import { isReadingPanePositionOff as getIsReadingPanePositionOff, isReadingPanePopoutOnly as getIsReadingPanePopoutOnly, } from "owa-mail-layout";
import PropertyIcons from "owa-mail-list-actions/lib/utils/conversationProperty/PropertyIcons";
import { showMailItemContextMenu } from "owa-mail-list-actions/lib/actions/itemContextMenuActions";
import MailListContextMenuSource from "owa-mail-list-store/lib/store/schema/MailListContextMenuSource";
import MailListContextMenuType from "owa-mail-list-store/lib/store/schema/MailListContextMenuType";
import { MailListItemExpansion } from "../lazyFunctions";
import mailStore from "owa-mail-store/lib/store/Store";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { getAnchorForContextMenu } from "owa-positioning";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import type { ITouchHandlerParams } from "owa-touch-handler";
import { touchHandler } from "owa-touch-handler";
import { ConditionalFormattingModificationType } from "owa-conditional-formatting";
import React from "react";
import type { TableView } from "owa-mail-list-store";
import { type TableQueryType, getStore as getListViewStore, getListViewState } from "owa-mail-list-store";
import { getMailListDragPreview, MAIL_ITEM_DRAG_XOFFSET, MAIL_ITEM_DRAG_YOFFSET, } from "../utils/mailListDragUtil";
import { lightColorCodeValueMap } from "owa-conditional-formatting/lib/utils/getConditionalFormattingLightThemeColorCodeValueMap";
import { darkColorCodeValueMap } from "owa-conditional-formatting/lib/utils/getConditionalFormattingDarkThemeColorCodeValueMap";
import { getIsDarkTheme } from "owa-fabric-theme";
import { isFirstLevelExpanded, isSecondLevelExpanded, isLoadingSecondLevelExpansion, } from "owa-mail-list-store/lib/selectors/isConversationExpanded";
import { isRowKeyInRowInsertionAnimation } from "owa-mail-list-item-animation-store";
import { getShouldAnimate } from "owa-mail-animation-coordination";
import { deactivateSRPForContextMenu, setLastAutoFocusedRowKey, } from "owa-mail-list-selection-actions-v2";
import useMailListItemDragEvents from "../hooks/useMailListItemDragEvents";
import useMailListItemFocusState from "../hooks/useMailListItemFocusState";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { animationContainer, contentContainer, } from "owa-expansion-animation/lib/styles/expansionAnimationStyles.scss";
import { transitionStyles } from "owa-expansion-animation";
import { CSSTransition } from "react-transition-group";
import { useIntersectionObserver } from "owa-react-hooks/lib/useIntersectionObserver";
import { animationStyles, hideAnimation, hcBorder, } from "owa-mail-listitem-styles/lib/scss/MailListItem.scss";
import { mailListItemContainer } from "owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss";
import { MailListItemWithTouchBehavior, MailListItemWithoutTouchBehavior, } from "./MailListItemWithTouchBehavior";
import getAriaLabelForMailListItemRow from "../utils/getAriaLabelForMailListItemRow";
import classnames from "owa-classnames";
import { addEventTimeToSourceMapping, safeRequestAnimationFrame } from "owa-performance";
import appendCheckpointToSMI from "owa-mail-logging/lib/utils/appendCheckpointToSMI";
import { isLocalDataBackedMailboxInfo } from "owa-account-source-list/lib/utils/isLocalDataBackedAccount";
import { addItemDataBase64Blob, hasItemDataBase64Blob, removeItemDataBase64Blob, } from "owa-mail-list-view-pst/lib/utils/itemBase64DataForPST";
import { lazyDragToDownloadEmails, lazyCacheMessageToDragAndDrop, lazyCacheMessageOnClick, } from "owa-mail-list-view-dnd";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import type { MailboxInfo } from "owa-client-types";
import { baseMailListItemColor } from "owa-mail-listitem-styles/lib/scss/MailListItemBackgroundColors.scss";
import { lazyWin32GqlSyncClient } from "native-win32gql-service-sync";
import { lazySyncItemExportPst } from "owa-mail-triage-movecopy-importexport";
import type { ExportItemResult } from "owa-graph-schema";
import { useRenderStats } from "owa-analytics";

export interface MailListItemContainerProps {
    mailListTableProps: MailListTableProps;
    mailListItemDataProps: MailListItemDataProps;
    resetListViewContentTabIndex: () => void;
    getIsFocusInMailList: () => boolean;
    styleSelectorAsPerUserSettings: string;
    renderMinimum: boolean;
    listRef: HTMLDivElement | null;
}

function useClearFocusOnUnmount(listViewItem: React.RefObject<HTMLDivElement | null>, renderMinimum: boolean) {
    React.useLayoutEffect(() => {
        const listViewItemElement = listViewItem.current;
        
        // Run before DOM removal, while the row can still contain the active element.
        return () => {
            // If item about to be unmounted had focus, set focus to the mail list component immediately so we do not
            // lose focus to body.
            if (!renderMinimum && listViewItemElement?.contains(document.activeElement)) {
                setFocusToSynchronous("MailList");
            }
        };
    }, []);
}

function useUpdateFocusOnFocusChange(listViewItem: React.RefObject<HTMLDivElement | null>, shouldRowBeFocused: boolean, shouldRowBeAutoFocused: boolean, getIsFocusInMailList: () => boolean, resetListViewContentTabIndex: () => void, tableView: TableView | undefined, rowKey: string, renderMinimum: boolean, listRef: HTMLDivElement | null, mailboxInfo: MailboxInfo) {
    const listViewState = getListViewState();
    const resetListViewContentTabIndexRef = React.useRef(resetListViewContentTabIndex);
    resetListViewContentTabIndexRef.current = resetListViewContentTabIndex;
    
    const isFocusInMailList = getIsFocusInMailList();
    
    const effectFunc = () => {
        const currentItem = listViewItem.current;
        
        if (tableView && currentItem) {
            if (shouldRowBeFocused) {
                if (listViewState.isFocusWithinJumpTo || listViewState.shouldAutoOpenJumpTo) {
                    // If focus is within the jump to dropdown, we do not want to auto focus any mail list item.
                    return;
                }
                
                // Check whether focus is in the listview before setting focus on item. There are cases in which focused row
                // might change when the focus is in a different component like compose, in which we do NOT want to focus to
                // go to a mail list item. Example: when in the unread filter, if user clicks on an item and replies, the item
                // disappears from list view and the next item gets selected. If this check wasn't here, then focus
                // would jump from compose to that newly selected mail list item.
                if (isFocusInMailList) {
                    if (shouldRowBeAutoFocused) {
                        // Focus on self if ref exists and the item is focused and focus is in mail list already
                        // We need state checks here because this is being called async on requestAnimationFrame.
                        currentItem.focus();
                        // Add the focus class when auto-focusing
                        setLastAutoFocusedRowKey(tableView, rowKey);
                        appendCheckpointToSMI("ml_RowFocus");
                    }
                    
                    currentItem.setAttribute("tabindex", tabIndex.sequentialIndex);
                }
                
                // Reset listview content tabindex such that listview is
                // not in a sequential keyboard navigation anymore once an item selected
                resetListViewContentTabIndexRef.current();
            }
            else {
                // Clear tab index on item when it's no longer focused
                // We need state checks here because this is being called async on requestAnimationFrame
                currentItem.setAttribute("tabindex", tabIndex.nonSequentialIndex.toString());
            }
        }
    };
    
    React.useEffect(() => {
        let animationFrameId: number | undefined;
        if (!renderMinimum) {
            animationFrameId = safeRequestAnimationFrame(effectFunc);
        }
        return () => {
            if (typeof animationFrameId == "number") {
                self.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [shouldRowBeFocused, shouldRowBeAutoFocused, isFocusInMailList, tableView?.id, rowKey]);
    
    const intersectionCallback = React.useCallback((entry: IntersectionObserverEntry) => {
        if (tableView &&
            listViewItem.current &&
            shouldRowBeFocused &&
            !isFocusInMailList &&
            shouldRowBeAutoFocused &&
            !entry.isIntersecting) {
            setLastAutoFocusedRowKey(tableView, rowKey);
        }
    }, [
        shouldRowBeFocused,
        shouldRowBeAutoFocused,
        isFocusInMailList,
        tableView,
        rowKey,
        mailboxInfo,
    ]);
    
    useIntersectionObserver("MailListItemContainer_main", listViewItem, intersectionCallback, {
        root: listRef,
        threshold: 0.1,
    });
}

function useLazyEvaluatedRowAriaInfo(shouldRowBeFocused: boolean, tableView: TableView | undefined, props: Pick<MailListItemContainerProps, "mailListItemDataProps">) {
    const numRows = React.useRef<number>(undefined);
    const rowPos = React.useRef<number>(undefined);
    
    return useComputedValue(() => {
        // ONLY update the num rows / row position for aria if the row is focused, otherwise all rows will be rerendered
        // which is a HUGE perf impact!! The num rows or row position can change any time user does a row removal (delete, archive, move),
        // pin, receives a new email, does a bulk delete, etc.
        if (shouldRowBeFocused) {
            numRows.current = tableView
                ? tableView.tableQuery.type === 1
                    ? tableView.rowKeys.length
                    : tableView.totalRowsInView
                : 0;
            
            const rowKeyIndex = tableView?.rowKeys?.indexOf(props.mailListItemDataProps.rowKey) ?? -1;
            rowPos.current = rowKeyIndex + 1;
        }
        
        return { numRows: numRows.current, rowPos: rowPos.current };
    }, [tableView, props.mailListItemDataProps.rowKey]);
}

const MailListComponentName = "MailListItemContainer";
const defaultExport: React.FunctionComponent<MailListItemContainerProps> = observer(function MailListItemContainer(props: MailListItemContainerProps) {
    const listViewItem = React.useRef<HTMLDivElement | null>(null);
    const { mailListItemDataProps, mailListTableProps, renderMinimum, listRef } = props;
    const { tableViewId } = mailListTableProps;
    const tableView = getListViewStore().tableViews.get(tableViewId);
    let mailboxInfo = getMailboxInfo(tableView);
    const isFocusComputedEnabled = isGlobalFeatureEnabled("mon-mail-list-focus-computed");
    useRenderStats("ML_Render_Stats", mailboxInfo, "MLIC" /* component */, [] /* item or conversation ID. Not needed here */, {
        customData: {
            isFocusComputedEnabled,
        },
    });
    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        const rowKeyMailboxInfo = mailListItemDataProps.mailboxInfo;
        if (rowKeyMailboxInfo) {
            mailboxInfo = rowKeyMailboxInfo;
        }
    }
    const { conditionalFormattingModifications, effectiveMentioned, hasAttachment, importance, isFlagged, isPinned, itemClassIcon, lastDeliveryTimestamp, returnTime, rowKey, sendersOrRecipients, shouldShowSenderOnTop, showDraftsIndicator, subject, thirdLineText, unreadCount, meetingStartTime, copilotInboxScore, shouldShowExternalLabel, } = mailListItemDataProps;
    
    const nodeRef = React.useRef(null);
    const [onDragStart, onDragEnd] = useMailListItemDragEvents();
    
    React.useEffect(() => {
        // Cleanup function to remove item blob when the component unmounts
        if (isLocalDataBackedMailboxInfo(mailboxInfo)) {
            return () => {
                // Remove the data when the component unmounts
                // So that we free the memory and avoid memory leaks.
                removeItemDataBase64Blob(mailListItemDataProps.latestItemId);
            };
        }
        
        return;
    }, [mailListItemDataProps.latestItemId, mailboxInfo]);
    
    const syncItemExportPstRef = React.useRef<((itemId: string, exportFormatType: string) => ExportItemResult) | undefined>(undefined);
    const dragToDownloadEmailsRef = React.useRef<((dataTransfer: DataTransfer, itemIds: string[], subjects: string[], mailboxInfos: MailboxInfo[]) => void) | undefined>(undefined);
    
    React.useEffect(() => {
        if (isLocalDataBackedMailboxInfo(mailboxInfo)) {
            lazyWin32GqlSyncClient
                .importAndExecute()
                .then(client => {
                return lazySyncItemExportPst.import().then(module => {
                    syncItemExportPstRef.current = (itemId: string, exportFormatType: string) => module(client, itemId, exportFormatType, mailboxInfo);
                });
            })
                .catch(() => {
                // Native host interop may fail — silently degrade
            });
        }
    }, [mailboxInfo]);
    
    React.useEffect(() => {
        lazyDragToDownloadEmails
            .import()
            .then(module => {
            dragToDownloadEmailsRef.current = module;
        })
            .catch(() => {
            // Lazy import failure — drag-to-download will be unavailable
        });
    }, []);
    
    const onDragStartFunc = React.useCallback((dataTransfer: DataTransfer): void => {
        onDragStart();
        const dragData = getDragData();
        if (dragData) {
            if (syncItemExportPstRef.current &&
                !hasItemDataBase64Blob(mailListItemDataProps.latestItemId)) {
                // PST files only support MSG format
                const exportResult = syncItemExportPstRef.current(mailListItemDataProps.latestItemId, "MSG");
                
                if (exportResult &&
                    exportResult.successful &&
                    exportResult.data &&
                    exportResult.blobType) {
                    addItemDataBase64Blob(mailListItemDataProps.latestItemId, exportResult.data, exportResult.blobType);
                }
            }
            
            if (dragToDownloadEmailsRef.current) {
                dragToDownloadEmailsRef.current(dataTransfer, dragData.itemIds, dragData.subjects, dragData.mailboxInfos);
            }
        }
    }, [
        mailListItemDataProps.latestItemId,
        mailListItemDataProps.subject,
        onDragStart,
        mailboxInfo,
        syncItemExportPstRef,
        dragToDownloadEmailsRef,
    ]);
    
    const mailListItemContainerContextValue = React.useMemo(() => ({
        conditionalFormattingModifications,
    }), [conditionalFormattingModifications]);
    
    /**
     * Programmatically reset hover if pinned state AND row key position have
     * changed (because we can assume that the row is no longer hovered).
     */
    const lastKnownRowKeyIndex = React.useRef(tableView?.rowKeys.indexOf(rowKey));
    React.useLayoutEffect(() => {
        const currentRowKeyIndex = tableView?.rowKeys.indexOf(rowKey);
        if (currentRowKeyIndex !== lastKnownRowKeyIndex.current) {
            lastKnownRowKeyIndex.current = currentRowKeyIndex;
        }
    }, [isPinned, tableView, tableView?.rowKeys, rowKey]);
    
    const { shouldRowBeFocused, shouldRowBeAutoFocused } = useMailListItemFocusState(tableView, tableViewId, rowKey, isFocusComputedEnabled);
    
    const isMLGroupedByConversation = tableView?.tableQuery.listViewType === 0;
    
    const { isFirstLevelExpansion, isSecondLevelExpansion, isLoadingSecondLevel } = useComputedValue(() => ({
        isFirstLevelExpansion: isFirstLevelExpanded(rowKey) &&
            (getListViewState().expandedConversationViewState.forks?.length ?? 0) > 1,
        isSecondLevelExpansion: isMLGroupedByConversation && isSecondLevelExpanded(rowKey),
        isLoadingSecondLevel: isMLGroupedByConversation && isLoadingSecondLevelExpansion(rowKey),
    }), [rowKey, isMLGroupedByConversation]);
    
    /**
     * The onContextMenu event handler which opens the context menu
     * @param evt the mouse event
     */
    const onItemContextMenu = React.useCallback((evt: React.MouseEvent<HTMLElement> | KeyboardEvent | React.TouchEvent<HTMLElement>) => {
        addEventTimeToSourceMapping("nativeEvent" in evt ? evt.nativeEvent : evt, MailListComponentName);
        evt.stopPropagation();
        evt.preventDefault();
        
        const selectedRows = tableView?.selectedRowKeys;
        
        // Select the row upon right click if
        // - user is not in selectAll mode
        // - and the row that clicked on is not selected yet
        if (tableView &&
            !tableView.isInVirtualSelectAllMode &&
            (!selectedRows || !selectedRows.has(rowKey))) {
            onMailListItemClickHandler(evt, MailListItemSelectionSource.MailListItemContextMenu, rowKey, tableViewId);
        }
        else if (tableView && !tableView.isInVirtualSelectAllMode) {
            // Row is already selected so no selection change is needed, but we still
            // need to deactivate the SRP tab. Without this, the context menu's
            // folder-based action checks (e.g. Archive) would use the SRP item's origin
            // folder instead of the mail list's current folder.
            deactivateSRPForContextMenu();
        }
        
        showMailItemContextMenu(getAnchorForContextMenu(evt), MailListContextMenuType.All, MailListContextMenuSource.MailListItem, listViewItem.current);
    }, [tableViewId, rowKey, tableView]);
    
    useClearFocusOnUnmount(listViewItem, renderMinimum);
    
    const { getIsFocusInMailList, resetListViewContentTabIndex } = props;
    useUpdateFocusOnFocusChange(listViewItem, shouldRowBeFocused, shouldRowBeAutoFocused, getIsFocusInMailList, resetListViewContentTabIndex, tableView, rowKey, renderMinimum, listRef, mailboxInfo);
    
    const shouldShowTwisty = props.mailListItemDataProps.shouldShowTwisty;
    const lazyKeyProps = React.useMemo(() => ({
        tableViewId,
        rowKey,
        shouldShowTwisty,
    }), [tableViewId, rowKey, shouldShowTwisty]);
    useLazyKeydownHandler(listViewItem, lazySetupMailListItemContainerKeys.importAndExecute, lazyKeyProps, renderMinimum /* don't attach keyboard handlers while scrolling */);
    useKeydownHandler(listViewItem, "shift+f10", onItemContextMenu, undefined, renderMinimum /* don't attach keyboard handlers while scrolling */);
    
    const touchHandlerProps = React.useMemo(() => {
        const touchHandlerParams: ITouchHandlerParams = {
            onLongPress: onItemContextMenu,
        };
        return touchHandler(touchHandlerParams);
    }, [onItemContextMenu]);
    
    const onKeyDown = React.useCallback((ev: React.KeyboardEvent<HTMLDivElement>) => {
        addEventTimeToSourceMapping(ev.nativeEvent, MailListComponentName);
        
        // Detect Space and prevent default to avoid scrolling
        if (ev.key === " ") {
            ev.preventDefault();
        }
    }, [rowKey, tableViewId]);
    
    const onMailListItemExpansionUnmount = React.useCallback(() => {
        // Bug 180055 React.useEffect cleanup can't reliably be used to conditionally move focus away from an "about to unmount" focused element
        // This callback is invoked by useEffect cleanup in MailListItemExpansion, which will run asynchronously _after_ the component has
        // been unmounted. Since below logic is checking for focus of listViewItem.current, a ref associated with a div rendered by this
        // component (MailListItemContainer), this should technically be okay. But, it is ill advised since this is performing DOM mutations
        // which should only be done during useLayoutEffect, not useEffect.
        
        // Recapture focus when mail list item expansion collapses if selection stays on current conversation (up arrow collapse). If you're on the
        // bottom most item part and hit the down arrow, we collapse the expansion and shift focus/selection to the next conversation.
        if (listViewItem.current) {
            if (!tableView) {
                return;
            }
            const selectedRowKey = [...tableView.selectedRowKeys.keys()][0];
            if (shouldRowBeFocused && selectedRowKey === rowKey) {
                listViewItem.current.focus();
            }
        }
    }, [shouldRowBeFocused]);
    
    const isReadingPanePositionFillScreen = useComputedValue(() => {
        // If user is in popout only mode, then we want to handle the onDoubleClick
        // instead of ignoring it to provide a cleaner experience and ensure
        // double-click is respected.
        return getIsReadingPanePositionOff() && !getIsReadingPanePopoutOnly();
    }, []);
    
    const onClick = React.useCallback((evt: React.MouseEvent<unknown, MouseEvent>) => {
        addEventTimeToSourceMapping(evt.nativeEvent, MailListComponentName);
        
        onMailListItemClickHandler(evt, MailListItemSelectionSource.MailListItemBody, rowKey, tableViewId);
        
        lazyCacheMessageOnClick.importAndExecute(mailListItemDataProps.latestItemId, mailListItemDataProps.subject ? mailListItemDataProps.subject : loc(noSubject), mailListItemDataProps.mailboxInfo ?? getModuleContextMailboxInfo());
    }, [rowKey, tableViewId]);
    
    const onDoubleClick = React.useCallback((evt: React.MouseEvent<unknown, MouseEvent>) => {
        addEventTimeToSourceMapping(evt.nativeEvent, MailListComponentName);
        evt.stopPropagation();
        if (mailListTableProps.supportsDoubleClick) {
            // Double-click is supported in "fill screen" RP mode by debouncing
            // the click handler (no need to rely on the double-click event)
            if (isReadingPanePositionFillScreen) {
                return;
            }
            
            onMailListItemClickHandler(evt, MailListItemSelectionSource.MailListItemBodyDoubleClick, rowKey, tableViewId);
        }
    }, [
        mailListTableProps.supportsDoubleClick,
        rowKey,
        tableViewId,
        isReadingPanePositionFillScreen,
    ]);
    
    const isSelected = mailListItemDataProps.isSelected;
    
    // In checked mode we have the parent container be draggable
    // Disable dragging if folderId or parentFolderId is of Notes Folder
    const canDrag = React.useCallback(() => {
        return (!mailListItemDataProps.isSelected &&
            mailListTableProps.canDragFromTable &&
            !mailListItemDataProps.isInNotesFolder);
    }, [
        mailListItemDataProps.isSelected,
        mailListTableProps.canDragFromTable,
        mailListItemDataProps.isInNotesFolder,
    ]);
    
    const isDraggable = React.useCallback(() => {
        return !mailListItemDataProps.isInGroup;
    }, [mailListItemDataProps.isInGroup]);
    
    const getItemSize = React.useCallback((itemId: string) => {
        const item = mailStore().items.get(itemId);
        return item?.Size;
    }, []);
    
    const getMailboxInfoForItem = React.useCallback((itemId: string) => {
        const item = mailStore().items.get(itemId);
        return item?.MailboxInfo ?? getModuleContextMailboxInfo();
    }, []);
    
    const getHasIRM = React.useCallback((itemId: string) => {
        const item = mailStore().items.get(itemId);
        return (!!item?.RightsManagementLicenseData ||
            mailListItemDataProps.itemClassIcon === PropertyIcons.MailListViewIRM ||
            mailListItemDataProps.itemClassIcon === PropertyIcons.MailListViewIRMReplied ||
            mailListItemDataProps.itemClassIcon === PropertyIcons.MailListViewIRMForwarded);
    }, []);
    
    const getDragData = React.useCallback(() => {
        return {
            itemType: DraggableItemTypes.MailListRow,
            rowKeys: [rowKey],
            tableViewId,
            tableListViewType: tableView?.tableQuery?.listViewType || 1,
            subjects: [
                mailListItemDataProps.subject ? mailListItemDataProps.subject : loc(noSubject),
            ],
            haveIRM: [getHasIRM(mailListItemDataProps.latestItemId)],
            sizes: [getItemSize(mailListItemDataProps.latestItemId)],
            mailboxInfos: [
                mailListItemDataProps.mailboxInfo ??
                    getMailboxInfoForItem(mailListItemDataProps.latestItemId),
            ],
            latestItemIds: [mailListItemDataProps.latestItemId],
            itemIds: [mailListItemDataProps.latestItemId],
            nodeIds: [],
            // We need to pass the single mailboxInfo as itemData
            // because canDrop does not have all data related to MailListItemPartDragData type
            itemData: {
                mailboxInfo: mailListItemDataProps.mailboxInfo ??
                    getMailboxInfoForItem(mailListItemDataProps.latestItemId),
            },
        };
    }, [mailListItemDataProps, tableView, rowKey, tableViewId]);
    
    const { numRows = 0, rowPos = 0 } = useLazyEvaluatedRowAriaInfo(shouldRowBeFocused, tableView, props);
    const isRowExpanded = isFirstLevelExpansion || isSecondLevelExpansion;
    
    const isChecked = useComputedValue(() => getIsChecked(tableView, rowKey, lastDeliveryTimestamp), [tableViewId, rowKey, lastDeliveryTimestamp]);
    
    const isPendingSelection = useComputedValue(() => tableView?.rowKeyPendingSelection === rowKey, [tableViewId, rowKey]);
    
    const backgroundColorClass = getBackgroundColorClass(isSelected, isFlagged, isChecked, mailListTableProps.supportsFlagging, isRowExpanded, isPendingSelection);
    
    const isSingleLine = mailListTableProps.isSingleLine;
    
    const labelForMailItem = useComputedValue(() => {
        return renderMinimum
            ? ""
            : getAriaLabelForMailListItemRow(tableView, tableViewId, shouldRowBeFocused, shouldShowTwisty /* canRowBeExpanded */, isFirstLevelExpansion || isSecondLevelExpansion /* isRowExpanded */, unreadCount, importance, hasAttachment, isFlagged, isPinned, subject, sendersOrRecipients, lastDeliveryTimestamp, thirdLineText, showDraftsIndicator, itemClassIcon, effectiveMentioned, returnTime, meetingStartTime, copilotInboxScore, mailboxInfo, shouldShowSenderOnTop, shouldShowExternalLabel /* shouldShowExternalLabel */);
    }, [
        renderMinimum,
        tableViewId,
        shouldRowBeFocused,
        shouldShowTwisty,
        isFirstLevelExpansion,
        isSecondLevelExpansion,
        unreadCount,
        importance,
        hasAttachment,
        isFlagged,
        isPinned,
        subject,
        sendersOrRecipients,
        lastDeliveryTimestamp,
        thirdLineText,
        showDraftsIndicator,
        itemClassIcon,
        effectiveMentioned,
        returnTime,
        meetingStartTime,
        copilotInboxScore,
        mailboxInfo,
        shouldShowSenderOnTop,
        shouldShowExternalLabel,
    ]);
    
    const itemAriaProps = React.useMemo((): AriaProperties => ({
        role: AriaRoles.option,
        selected: isSelected,
        label: labelForMailItem || undefined, // When no important information, do not add label
    }), [labelForMailItem, isSelected]);
    const hasExecutedRef = React.useRef(false);
    const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    
    const handleOnMouseLeave = React.useCallback(() => {
        if (isGlobalFeatureEnabled("mon-attachment-blob-cache")) {
            hasExecutedRef.current = false;
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
            }
        }
    }, [mailListItemDataProps.subject]);
    
    const handleOnMouseHover = React.useCallback(() => {
        if (isGlobalFeatureEnabled("mon-attachment-blob-cache")) {
            if (hasExecutedRef.current) {
                return;
            }
            
            // Clear any existing timeout before setting a new one
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
            
            hoverTimeoutRef.current = setTimeout(() => {
                if (!hasExecutedRef.current) {
                    hasExecutedRef.current = true;
                    const dragData = getDragData();
                    lazyCacheMessageToDragAndDrop
                        .importAndExecute(dragData.itemIds, dragData.subjects, dragData.mailboxInfos)
                        .then(() => {
                        hasExecutedRef.current = false;
                    });
                }
            }, 1000);
        }
    }, [mailListItemDataProps, tableView, rowKey, tableViewId, mailboxInfo]);
    
    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
            }
        };
    }, []);
    
    const isDarkTheme = getIsDarkTheme();
    const conditionalFormattingVars = React.useMemo<React.CSSProperties | undefined>(() => {
        if (!conditionalFormattingModifications ||
            conditionalFormattingModifications.length === 0) {
            return undefined;
        }
        
        // Find ColorTextAndBackground rule
        const colorMod = conditionalFormattingModifications.find(m => m.type === ConditionalFormattingModificationType.ColorTextAndBackground);
        if (!colorMod || colorMod.value === undefined || colorMod.value === null) {
            return undefined;
        }
        
        const map = isDarkTheme ? darkColorCodeValueMap : lightColorCodeValueMap;
        const entry = map[colorMod.value as keyof typeof map];
        if (!entry) {
            return undefined;
        }
        
        // Supply vars; we only set those the SCSS reads
        return {
            "--cf-row-bg": entry.restColor,
            "--cf-row-bg-hover": entry.hoverColor,
            "--cf-row-bg-secondary": unreadCount === 0 ? undefined : entry.buttonColor,
            "--cf-row-bg-active": entry.activeColor,
            "--cf-row-bg-selected": entry.activeColor,
            "--cf-text-hover-color": entry.textSelectedHoverColor,
        } as React.CSSProperties;
    }, [
        conditionalFormattingModifications.length,
        isSelected,
        isChecked,
        isFlagged,
        isDarkTheme,
        unreadCount,
    ]);
    
    const hasAnimationsEnabled = !renderMinimum && getShouldAnimate();
    const shouldHideAnimation = useComputedValue(() => isRowKeyInRowInsertionAnimation(rowKey), [rowKey]);
    const expansionWrapperId = `${rowKey}_expansionAnimationWrapper`;
    const MailListItemComponent = renderMinimum
        ? MailListItemWithoutTouchBehavior
        : MailListItemWithTouchBehavior;
    
    return (<MailListItemContainerContextProvider value={mailListItemContainerContextValue}>
                <div {...touchHandlerProps} id={rowKey} data-convid={mailListItemDataProps.conversationId} ref={listViewItem} tabIndex={-1} className={classnames(mailListItemContainer, hasAnimationsEnabled ? animationStyles : undefined, hasAnimationsEnabled && shouldHideAnimation ? hideAnimation : undefined, isSelected && getIsMsHighContrast() && hcBorder, backgroundColorClass, baseMailListItemColor)} onClick={onClick} onDoubleClick={onDoubleClick} aria-selected={isSelected} aria-setsize={numRows} aria-posinset={rowPos} onContextMenu={onItemContextMenu} onKeyDown={onKeyDown} {...generateDomPropertiesForAria(itemAriaProps)} data-focusable-row={true} onMouseOver={handleOnMouseHover} onMouseLeave={handleOnMouseLeave} style={conditionalFormattingVars}>
                    <Draggable canDrag={canDrag} isDraggable={isDraggable} getDragData={getDragData} getDragPreview={getMailListDragPreview} xOffset={MAIL_ITEM_DRAG_XOFFSET} yOffset={MAIL_ITEM_DRAG_YOFFSET} onDragStart={onDragStartFunc} onDragEnd={onDragEnd}>
                        <MailListItemComponent mailListTableProps={mailListTableProps} mailListItemDataProps={mailListItemDataProps} styleSelectorAsPerUserSettings={props.styleSelectorAsPerUserSettings} isFirstLevelExpanded={isFirstLevelExpansion} isSecondLevelExpanded={isSecondLevelExpansion} isLoadingSecondLevelExpansion={isLoadingSecondLevel} renderMinimum={renderMinimum}/>
                    </Draggable>
                </div>
                <CSSTransition id={expansionWrapperId} in={isRowExpanded} timeout={200} unmountOnExit={true} classNames={transitionStyles} nodeRef={nodeRef}>
                    <div className={animationContainer} ref={nodeRef}>
                        <div className={contentContainer}>
                            <MailListItemExpansion wrapperId={expansionWrapperId} onUnmount={onMailListItemExpansionUnmount} conversationId={mailListItemDataProps.rowId} tableViewId={tableViewId} showPreviewText={mailListTableProps.showPreviewText} numMessagePreviewLines={mailListTableProps.numMessagePreviewLines} isPinned={mailListItemDataProps.isPinned} isSingleLine={isSingleLine} tableViewSupportsFlagging={mailListTableProps.supportsFlagging} getIsFocusInMailList={props.getIsFocusInMailList} isFirstLevelExpansion={isFirstLevelExpansion} isSecondLevelExpansion={isSecondLevelExpansion} rowKey={rowKey} supportsPinning={mailListTableProps.supportsPinning} parentItemSupportsMove={mailListTableProps.supportsMove} parentItemCanArchive={mailListItemDataProps.canArchive} parentItemCanPin={mailListItemDataProps.canPin}/>
                        </div>
                    </div>
                </CSSTransition>
            </MailListItemContainerContextProvider>);
}, MailListComponentName);
export default defaultExport;
